const byId = (pattern = '', id = '') => pattern.replace(':id', id);
const byAOneId = (pattern = '', aOneId = '') => pattern.replace(':aOneId', aOneId);
const byUserId = (pattern = '', userId = '') => pattern.replace(':userId', userId);
const byJobId = (pattern = '', jobId = '') => pattern.replace(':jobId', jobId);
const byWsslug = (pattern = '', wsSlug = '') => pattern.replace(':wsSlug', wsSlug);
const byOrgSlug = (pattern = '', orgSlug = '') => pattern.replace(':orgSlug', orgSlug);
const byTeamSlug = (pattern = '', teamSlug = '') => pattern.replace(':teamSlug', teamSlug);
const byRepoSlug = (pattern = '', repoSlug = '') => pattern.replace(':repoSlug', repoSlug);
const byBillingAccountId = (pattern = '', billingAccountId = '') =>
  pattern.replace(':billingAccountId', billingAccountId);

type TRoute = {
  pattern: string;
  make: (orgSlug?: string, aOneId?: string, wsSlug?: string, id?: string) => string;
};

type TRoutes = {
  [key: string]: TRoute;
};

export const routes: TRoutes = {
  root: {
    pattern: '/',
    make() {
      return this.pattern;
    },
  },

  contactSales: {
    pattern: '/contact-sales',
    make() {
      return this.pattern;
    },
  },

  paymentSuccess: {
    pattern: '/payment/success',
    make() {
      return this.pattern;
    },
  },
  paymentFailure: {
    pattern: '/payment/failure',
    make() {
      return this.pattern;
    },
  },

  adminDiff: {
    pattern: '/admin/diff',
    make() {
      return this.pattern;
    },
  },

  adminTranscriptReview: {
    pattern: '/admin/transcript-review',
    make() {
      return this.pattern;
    },
  },

  adminTranscriptReviewJob: {
    pattern: '/admin/transcript-review/:jobId',
    make(jobId = '') {
      return byJobId(this.pattern, jobId);
    },
  },

  adminPrompts: {
    pattern: '/admin/prompts',
    make() {
      return this.pattern;
    },
  },

  adminNoticeExtractor: {
    pattern: '/admin/notice-extractor',
    make() {
      return this.pattern;
    },
  },

  adminComponentShowcase: {
    pattern: '/admin/component-showcase',
    make() {
      return this.pattern;
    },
  },

  adminAsrProxyConsole: {
    pattern: '/admin/asr-proxy',
    make() {
      return this.pattern;
    },
  },

  adminAsrProxyTranscription: {
    pattern: '/admin/asr-proxy',
    make(transcriptionId = '') {
      return `${this.pattern}?transcription=${encodeURIComponent(transcriptionId)}`;
    },
  },

  adminAsrProxyJobsSearch: {
    pattern: '/admin/asr-proxy',
    make(search = '') {
      return `${this.pattern}?tab=jobs&search=${encodeURIComponent(search)}`;
    },
  },

  adminApiDocs: {
    pattern: '/admin/api-docs',
    make() {
      return this.pattern;
    },
  },

  adminApiDocsOrganizationApi: {
    pattern: '/admin/api-docs/organization-api',
    make() {
      return this.pattern;
    },
  },

  adminApiDocsInternalApi: {
    pattern: '/admin/api-docs/internal-api',
    make() {
      return this.pattern;
    },
  },

  adminDashboard: {
    pattern: '/admin/dashboard',
    make() {
      return this.pattern;
    },
  },

  notifications: {
    pattern: '/notifications',
    make() {
      return this.pattern;
    },
  },

  notificationSettings: {
    pattern: '/notifications/settings',
    make() {
      return this.pattern;
    },
  },

  adminWorkspacesList: {
    pattern: '/admin/workspaces',
    make() {
      return this.pattern;
    },
  },

  adminWorkspaceDetails: {
    pattern: '/admin/workspaces/:id',
    make(id = '') {
      return byId(this.pattern, id);
    },
  },

  adminRepositoryDetails: {
    pattern: '/admin/repository/:id',
    make(id = '') {
      return byId(this.pattern, id);
    },
  },

  adminTeamDetails: {
    pattern: '/admin/team/:id',
    make(id = '') {
      return byId(this.pattern, id);
    },
  },

  adminSettings: {
    pattern: '/admin/settings',
    make() {
      return this.pattern;
    },
  },

  adminOrganizationsList: {
    pattern: '/admin/organizations',
    make() {
      return this.pattern;
    },
  },
  adminCloudLayouts: {
    pattern: '/admin/cloud-layouts',
    make() {
      return this.pattern;
    },
  },

  adminOrganizationDetails: {
    pattern: '/admin/organizations/:id',
    make(id = '') {
      return byId(this.pattern, id);
    },
  },

  adminAdminsList: {
    pattern: '/admin/admins',
    make() {
      return this.pattern;
    },
  },

  adminCanvasCoursesList: {
    pattern: '/admin/canvas/courses',
    make() {
      return this.pattern;
    },
  },

  adminCanvasCourseStudentsPage: {
    pattern: '/admin/canvas/courses/:id/students',
    make(id = '') {
      return byId(this.pattern, id);
    },
  },

  adminOrganizationUsersList: {
    pattern: '/admin/organizations/:id/members',
    make(id = '') {
      return byId(this.pattern, id);
    },
  },

  adminOrganizationInvitationsList: {
    pattern: '/admin/organizations/:id/invitations',
    make(id = '') {
      return byId(this.pattern, id);
    },
  },

  adminUsersList: {
    pattern: '/admin/users',
    make() {
      return this.pattern;
    },
  },

  adminAuditLogs: {
    pattern: '/admin/audit-logs',
    make() {
      return this.pattern;
    },
  },

  adminBillingAccountsList: {
    pattern: '/admin/billing-accounts',
    make() {
      return this.pattern;
    },
  },

  adminAttorneysAccess: {
    pattern: '/admin/attorneys-access',
    make() {
      return this.pattern;
    },
  },

  adminAttorneyUsers: {
    pattern: '/admin/attorney-users',
    make() {
      return this.pattern;
    },
  },

  adminAttorneysPasscodes: {
    pattern: '/admin/attorney-passcodes',
    make() {
      return this.pattern;
    },
  },

  adminJobsReport: {
    pattern: '/admin/jobs-report',
    make() {
      return this.pattern;
    },
  },

  adminUserDetails: {
    pattern: '/admin/users/:userId',
    make(userId = '') {
      return byUserId(this.pattern, userId);
    },
  },

  adminJobDetails: {
    pattern: '/admin/job-details/:id',
    make(id = '') {
      return byId(this.pattern, id);
    },
  },

  adminJobUtterances: {
    pattern: '/admin/job-details/:id/utterances',
    make(id = '') {
      return byId(this.pattern, id);
    },
  },

  adminJobTranscriptPlayer: {
    pattern: '/admin/job-details/:id/transcript-player',
    make(id = '') {
      return byId(this.pattern, id);
    },
  },

  adminSSOProvidersList: {
    pattern: '/admin/sso-providers',
    make() {
      return this.pattern;
    },
  },

  adminSubscriptions: {
    pattern: '/admin/subscriptions',
    make() {
      return this.pattern;
    },
  },

  adminTransactions: {
    pattern: '/admin/transactions',
    make() {
      return this.pattern;
    },
  },
  adminTransactionsForBillingAccount: {
    pattern: '/admin/transactions/billing-account/:billingAccountId',
    make(billingAccountId = '') {
      return byBillingAccountId(this.pattern, billingAccountId);
    },
  },

  adminSMSCountries: {
    pattern: '/admin/sms-countries',
    make() {
      return this.pattern;
    },
  },

  adminSMSWhitelist: {
    pattern: '/admin/sms-whitelist',
    make() {
      return this.pattern;
    },
  },

  adminFeatureFlags: {
    pattern: '/admin/feature-flags',
    make() {
      return this.pattern;
    },
  },

  adminAttorneyViewFeatureFlags: {
    pattern: '/admin/feature-flags/attorney-view',
    make() {
      return this.pattern;
    },
  },

  adminBillingAccountDetails: {
    pattern: '/admin/billing-account/:id',
    make(billingAccountId = '') {
      return byId(this.pattern, billingAccountId);
    },
  },

  adminAutoScriptOneSubscriptionDetails: {
    pattern: '/admin/subscription/:aOneId',
    make(aOneId = '') {
      return byAOneId(this.pattern, aOneId);
    },
  },

  adminTransactionCodesList: {
    pattern: '/admin/transaction-codes',
    make() {
      return this.pattern;
    },
  },

  shareJob: {
    pattern: '/share/job/:jobId',
    make(jobId = '') {
      return byJobId(this.pattern, jobId);
    },
  },

  openInASOne: {
    pattern: '/as-one/job/:id',
    make(recordingSessionId = '') {
      return byId(this.pattern, recordingSessionId);
    },
  },

  noOrgs: {
    pattern: '/create-organization',
    make() {
      return this.pattern;
    },
  },

  accountPage: {
    pattern: '/account',
    make() {
      return this.pattern;
    },
  },

  organizationApiDocs: {
    pattern: '/api-docs/organization-api',
    make() {
      return this.pattern;
    },
  },

  desktopAppHelp: {
    pattern: '/desktop-app',
    make() {
      return this.pattern;
    },
  },

  training: {
    pattern: '/training',
    make() {
      return this.pattern;
    },
  },

  trainingEditorProofer: {
    pattern: '/training/editor-proofer',
    make() {
      return this.pattern;
    },
  },

  trainingAgency: {
    pattern: '/training/agency',
    make() {
      return this.pattern;
    },
  },

  trainingFolder: {
    pattern: '/training/folder/:role/:folder',
    make(role = '', folder = '') {
      return this.pattern.replace(':role', encodeURIComponent(role)).replace(':folder', encodeURIComponent(folder));
    },
  },

  trainingWatch: {
    pattern: '/training/watch',
    make(src = '') {
      if (!src) {
        return this.pattern;
      }
      return `${this.pattern}?src=${encodeURIComponent(src)}`;
    },
  },

  trainingAdditionalResources: {
    pattern: '/training/additional-resources',
    make() {
      return this.pattern;
    },
  },

  signin: {
    pattern: '/signin',
    make() {
      return this.pattern;
    },
  },

  desktopSignin: {
    pattern: '/desktop/signin',
    make() {
      return this.pattern;
    },
  },
  accountDisabled: {
    pattern: '/account-disabled',
    make() {
      return this.pattern;
    },
  },
  rateLimit: {
    pattern: '/rate-limit',
    make() {
      return this.pattern;
    },
  },
  signup: {
    pattern: '/signup',
    make() {
      return this.pattern;
    },
  },

  ssoSignin: {
    pattern: '/sso/login',
    make() {
      return this.pattern;
    },
  },

  passwordRecovery: {
    pattern: '/password-recovery',
    make() {
      return this.pattern;
    },
  },
  passwordReset: {
    pattern: '/password-reset',
    make() {
      return this.pattern;
    },
  },
  registerSuccess: {
    pattern: '/register-success',
    make() {
      return this.pattern;
    },
  },
  registerTokenExpired: {
    pattern: '/register-token-expired',
    make() {
      return this.pattern;
    },
  },
  billingAccount: {
    pattern: '/:orgSlug/billing-account',
    make(orgSlug = '') {
      return byOrgSlug(this.pattern, orgSlug);
    },
  },

  autoScriptOneSubscriptionDetails: {
    pattern: '/:orgSlug/billing-account/subscription/:aOneId',
    make(orgSlug = '', aOneId = '') {
      return byAOneId(byOrgSlug(this.pattern, orgSlug), aOneId);
    },
  },

  organizationsList: {
    pattern: '/organizations',
    make() {
      return this.pattern;
    },
  },

  dashboard: {
    pattern: '/:orgSlug/dashboard',
    make(orgSlug = '') {
      return byOrgSlug(this.pattern, orgSlug);
    },
  },
  organizationDetails: {
    pattern: '/:orgSlug/details',
    make(slug = '') {
      return byOrgSlug(this.pattern, slug);
    },
  },
  organizationMembers: {
    pattern: '/:orgSlug/members',
    make(slug = '') {
      return byOrgSlug(this.pattern, slug);
    },
  },
  organizationAttorneysReport: {
    pattern: '/:orgSlug/attorneys-report',
    make(slug = '') {
      return byOrgSlug(this.pattern, slug);
    },
  },
  organizationTeams: {
    pattern: '/:orgSlug/teams',
    make(slug = '') {
      return byOrgSlug(this.pattern, slug);
    },
  },
  organizationMemberReports: {
    pattern: '/:orgSlug/member-reports',
    make(slug = '') {
      return byOrgSlug(this.pattern, slug);
    },
  },
  newRepository: {
    pattern: '/:orgSlug/case/new',
    make(slug = '') {
      return byOrgSlug(this.pattern, slug);
    },
  },
  repositoryDetails: {
    pattern: '/:orgSlug/case/:repoSlug',
    make(orgSlug = '', repoSlug = '', repoId = '') {
      return byOrgSlug(byRepoSlug(this.pattern, repoSlug && repoSlug !== 'new' ? repoSlug : repoId), orgSlug);
    },
  },
  repositoriesList: {
    pattern: '/:orgSlug/cases',
    make(orgSlug = '') {
      return byOrgSlug(this.pattern, orgSlug);
    },
  },
  userDetails: {
    pattern: '/:orgSlug/members/:userId',
    make(orgSlug = '', userId = '') {
      return byOrgSlug(byUserId(this.pattern, userId), orgSlug);
    },
  },
  invitations: {
    pattern: '/:orgSlug/invitations',
    make(orgSlug = '') {
      return byOrgSlug(this.pattern, orgSlug);
    },
  },
  allJobs: {
    pattern: '/:orgSlug/all-jobs',
    make(orgSlug = '') {
      return byOrgSlug(this.pattern, orgSlug);
    },
  },

  workspaceDetails: {
    pattern: '/:orgSlug/workspaces/:wsSlug/details',
    make(orgSlug = '', wsSlug = '') {
      return byOrgSlug(byWsslug(this.pattern, wsSlug), orgSlug);
    },
  },
  newJob: {
    pattern: '/:orgSlug/workspaces/:wsSlug/jobs/new',
    make(orgSlug = '', wsSlug = '') {
      return byOrgSlug(byWsslug(this.pattern, wsSlug), orgSlug);
    },
  },
  jobEdit: {
    pattern: '/:orgSlug/workspaces/:wsSlug/jobs/edit/:id',
    make(orgSlug = '', wsSlug = '', id = '') {
      return byOrgSlug(byWsslug(byId(this.pattern, id), wsSlug), orgSlug);
    },
  },
  jobDetails: {
    pattern: '/:orgSlug/workspaces/:wsSlug/jobs/details/:id',
    make(orgSlug = '', wsSlug = '', id = '') {
      return byOrgSlug(byWsslug(byId(this.pattern, id), wsSlug), orgSlug);
    },
  },
  noticeExtractor: {
    pattern: '/:orgSlug/workspaces/:wsSlug/notice-extractor',
    make(orgSlug = '', wsSlug = '') {
      return byOrgSlug(byWsslug(this.pattern, wsSlug), orgSlug);
    },
  },
  userJobsReport: {
    pattern: '/:orgSlug/workspaces/:wsSlug/job-reports/:userId',
    make(orgSlug = '', wsSlug = '', userId = '') {
      return byOrgSlug(byWsslug(byUserId(this.pattern, userId), wsSlug), orgSlug);
    },
  },
  jobsReport: {
    pattern: '/:orgSlug/workspaces/:wsSlug/job-reports',
    make(orgSlug = '', wsSlug = '') {
      return byOrgSlug(byWsslug(this.pattern, wsSlug), orgSlug);
    },
  },

  requestAccess: {
    pattern: '/:orgSlug/workspaces/:wsSlug/requestAccess',
    make(orgSlug = '', wsSlug = '') {
      return byOrgSlug(byWsslug(this.pattern, wsSlug), orgSlug);
    },
  },
  acceptInvitation: {
    pattern: '/invitation/:id/accept',
    make(id = '') {
      return byId(this.pattern, id);
    },
  },
  internalError: {
    pattern: '/errors/500',
    make() {
      return this.pattern;
    },
  },

  jobsList: {
    pattern: '/:orgSlug/workspaces/:wsSlug/jobs',
    make(orgSlug = '', wsSlug = '') {
      return byOrgSlug(byWsslug(this.pattern, wsSlug), orgSlug);
    },
  },

  teamDetails: {
    pattern: '/:orgSlug/team/:teamSlug',
    make(orgSlug = '', teamSlug = '') {
      return byOrgSlug(byTeamSlug(this.pattern, teamSlug), orgSlug);
    },
  },
};
